
import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, BarChart2, Shield, Users, CheckCircle, ArrowRightCircle, Target, TrendingUp, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const SchoolLeadersPage = () => {
  const fadeIn = (delay = 0, y = 20) => ({
    hidden: { opacity: 0, y: y, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, delay, ease: [0.6, -0.05, 0.01, 0.99] } },
  });

  const challenges = [
    {
      challenge: "Effectief welzijnsbeleid implementeren",
      solution: "Classbuddy integreert naadloos in uw bestaande zorgstructuur en biedt concrete data voor beleidsbeslissingen.",
      icon: <Target size={32} className="text-cm-blue" />
    },
    {
      challenge: "Vroegtijdige signalering verbeteren",
      solution: "Automatische detectie van risicosignalen helpt uw team sneller en gerichter te handelen.",
      icon: <TrendingUp size={32} className="text-cm-green" />
    },
    {
      challenge: "Werkdruk zorgteam verminderen",
      solution: "Efficiënte triage en gerichte aandacht voor kinderen die écht hulp nodig hebben.",
      icon: <Users size={32} className="text-cm-pink" />
    }
  ];

  const benefits = [
    {
      title: "Datagedreven beleid",
      description: "Concrete inzichten in welzijnsthema's op uw school om gefundeerde beslissingen te nemen over zorgbeleid en preventie.",
      icon: <BarChart2 size={36} className="text-cm-blue" />
    },
    {
      title: "Vroege interventie",
      description: "Signalen worden automatisch gedetecteerd voordat problemen escaleren, waardoor preventief handelen mogelijk wordt.",
      icon: <Shield size={36} className="text-cm-green" />
    },
    {
      title: "ROI in welzijn",
      description: "Investering in Classbuddy leidt tot verminderde uitval, betere sfeer en minder complexe zorgtrajecten.",
      icon: <Award size={36} className="text-cm-yellow" />
    }
  ];

  const metrics = [
    { label: "Vroegsignalering", value: "85%", description: "verbetering in tijdige detectie" },
    { label: "Teamefficiëntie", value: "60%", description: "minder tijd aan intake gesprekken" },
    { label: "Kindwelzijn", value: "92%", description: "van kinderen voelt zich gehoord" },
    { label: "Schoolklimaat", value: "78%", description: "verbetering in sociale veiligheid" }
  ];

  return (
    <div className="container mx-auto px-4 section-padding overflow-hidden">
      <motion.div
        initial="hidden"
        animate="visible"
        className="text-center mb-16"
      >
        <motion.div variants={fadeIn(0,30)} className="flex justify-center mb-8">
          <GraduationCap size={72} className="text-cm-green" />
        </motion.div>
        <motion.h1
          variants={fadeIn(0.1, 30)}
          className="page-title"
        >
          Voor <span className="text-gradient-pink-blue font-medium">Schoolleiders</span> & IB'ers
        </motion.h1>
        <motion.p
          variants={fadeIn(0.2, 20)}
          className="page-subtitle"
        >
          Transformeer uw welzijnsbeleid met datagedreven inzichten en effectieve vroegsignalering. Classbuddy helpt u een veiligere, gezondere schoolomgeving te creëren.
        </motion.p>
      </motion.div>

      {/* Leadership Challenges */}
      <motion.div variants={fadeIn(0.3, 20)} initial="hidden" animate="visible" className="mb-20">
        <h2 className="text-4xl font-sans font-medium text-cm-blue-dark mb-12 text-center">
          Uitdagingen die u <span className="text-gradient-pink-blue">herkent</span>
        </h2>
        <div className="space-y-8">
          {challenges.map((item, index) => (
            <motion.div
              key={index}
              variants={fadeIn(0.4 + index * 0.1, 20)}
              initial="hidden"
              animate="visible"
              className="glassmorphism-card p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-start space-x-6"
              whileHover={{ y: -5, scale: 1.01 }}
            >
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cm-green-light to-white rounded-full flex items-center justify-center shadow-md">
                {item.icon}
              </div>
              <div className="flex-grow">
                <h3 className="text-xl font-sans font-medium text-cm-blue-dark mb-3">{item.challenge}</h3>
                <p className="text-cm-gray leading-relaxed">{item.solution}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Strategic Benefits */}
      <motion.div variants={fadeIn(0.7, 20)} initial="hidden" animate="visible" className="mb-20">
        <h2 className="text-4xl font-sans font-medium text-cm-blue-dark mb-12 text-center">
          Strategische <span className="text-gradient-pink-blue">voordelen</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={fadeIn(0.8 + index * 0.1, 20)}
              initial="hidden"
              animate="visible"
              className="glassmorphism-card p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 text-center"
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="flex justify-center items-center mb-6 w-20 h-20 bg-gradient-to-br from-cm-blue-light to-white rounded-full shadow-md">
                {benefit.icon}
              </div>
              <h3 className="text-2xl font-sans font-medium text-cm-blue-dark mb-4">{benefit.title}</h3>
              <p className="text-cm-gray leading-relaxed">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Success Metrics */}
      <motion.div 
        variants={fadeIn(1.1, 20)} 
        initial="hidden" 
        animate="visible" 
        className="mb-20 p-10 bg-gradient-to-r from-cm-blue-light/30 to-cm-green-light/30 rounded-3xl shadow-lg"
      >
        <h2 className="text-4xl font-sans font-medium text-cm-blue-dark mb-8 text-center">
          Bewezen <span className="text-gradient-pink-blue">Resultaten</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {metrics.map((metric, index) => (
            <motion.div
              key={index}
              variants={fadeIn(1.2 + index * 0.1, 20)}
              initial="hidden"
              animate="visible"
              className="glassmorphism-card p-6 rounded-xl text-center"
              whileHover={{ y: -5, scale: 1.05 }}
            >
              <div className="text-4xl font-medium text-gradient-pink-blue mb-2">{metric.value}</div>
              <div className="text-lg font-semibold text-cm-blue-dark mb-1">{metric.label}</div>
              <div className="text-sm text-cm-gray">{metric.description}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Implementation Process */}
      <motion.div variants={fadeIn(1.5, 20)} initial="hidden" animate="visible" className="mb-20">
        <h2 className="text-4xl font-sans font-medium text-cm-blue-dark mb-12 text-center">
          Implementatie in <span className="text-gradient-pink-blue">4 stappen</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { step: "1", title: "Intake & Planning", description: "Kennismaking en maatwerk implementatieplan" },
            { step: "2", title: "Training Team", description: "Onboarding van uw zorgteam en leerkrachten" },
            { step: "3", title: "Pilot Start", description: "Geleidelijke uitrol met begeleiding" },
            { step: "4", title: "Evaluatie & Opschaling", description: "Resultaten meten en volledig implementeren" }
          ].map((phase, index) => (
            <motion.div
              key={index}
              variants={fadeIn(1.6 + index * 0.1, 20)}
              initial="hidden"
              animate="visible"
              className="glassmorphism-card p-6 rounded-xl text-center"
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-cm-pink to-cm-blue rounded-full flex items-center justify-center text-white font-medium text-xl mx-auto mb-4">
                {phase.step}
              </div>
              <h3 className="text-lg font-medium text-cm-blue-dark mb-2">{phase.title}</h3>
              <p className="text-sm text-cm-gray">{phase.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Call to Action */}
      <motion.div
        variants={fadeIn(2.0, 20)}
        initial="hidden"
        animate="visible"
        className="p-10 bg-gradient-to-r from-cm-green-light/70 via-cm-yellow-light/70 to-cm-pink-light/70 rounded-3xl shadow-2xl text-center"
      >
        <CheckCircle size={56} className="text-cm-green mx-auto mb-6" />
        <h2 className="text-4xl font-sans font-medium text-cm-blue-dark mb-5">Start uw welzijnstransformatie</h2>
        <p className="text-lg text-cm-gray mb-8 max-w-3xl mx-auto leading-relaxed">
          Neem de volgende stap naar een datagedreven welzijnsbeleid. Plan een strategische demo en ontdek hoe Classbuddy uw school kan helpen elk kind de zorg te geven die het verdient.
        </p>
        <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <Button asChild size="lg" className="bg-gradient-to-r from-cm-pink to-cm-blue hover:from-cm-pink-dark hover:to-cm-blue-dark text-white font-medium text-lg px-10 py-7 rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300 group">
            <Link to="/contact?subject=StrategischeDemo">
              Plan strategische demo <ArrowRightCircle className="ml-2.5 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="glassmorphism-button text-cm-blue-dark border-cm-blue hover:text-cm-pink hover:border-cm-pink font-medium text-lg px-10 py-7 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300 group">
            <Link to="/waarom-werkt-het">
              Bekijk de wetenschap <BarChart2 className="ml-2.5 h-5 w-5 group-hover:rotate-3 transition-transform" />
            </Link>
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default SchoolLeadersPage;
